<template>
  <template v-if="animated">
    <transition
      :name="transitionName"
      @before-enter="startTransition"
      @after-enter="endTransition"
    >
      <div v-show="isActive && visible" ref="root" class="step-item">
        <slot />
      </div>
    </transition>
  </template>

  <template v-else>
    <div v-show="isActive && visible" ref="root" class="step-item">
      <slot />
    </div>
  </template>
</template>

<script>
import { uuid, computedInstance } from '../../utils/helpers'

import {
  defineComponent,
  ref,
  computed,
  inject,
  onMounted,
  onBeforeUnmount,
} from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-step-item label="Step 1" clickable>
   *   <h1>Step 1</h1>
   * <ak-step-item>
   */
  name: 'AkStepItem',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {String}
     * @default undefined
     * @description Step label
     */
    label: {
      type: String,
      default: undefined,
    },

    /**
     * @type {String|Object}
     * @default undefined
     * @description Default Type/Style for the step, optional This will override
     * parent type. Could be used to set a completed step to "success" for example
     */
    type: {
      type: [String, Object],
      default: undefined,
    },

    /**
     * @type {String}
     * @default undefined
     * @description Icon name
     */
    icon: {
      type: String,
      default: undefined,
    },

    /**
     * @type {Boolean}
     * @default undefined
     * @description Item can be used directly to navigate. If undefined,
     * previous steps are clickable while the others are not.
     */
    clickable: {
      type: Boolean,
      default: undefined,
    },

    /**
     * @type {Boolean}
     * @default true
     * @description Item is visible
     */
    visible: {
      type: Boolean,
      default: true,
    },
  },

  /**
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const isActive = ref(false)
    const transitionName = ref(null)
    const isStepItem = ref(true)
    const parent = inject('akSteps')
    const animated = computed(() => parent && parent.animated)
    const root = ref(null)
    const uid = uuid()

    /**
     * @param {Number} oldIndex
     * @param {Number} index
     * @description Activate step, alter animation name based on the index.
     */
    const activate = (oldIndex, index) => {
      transitionName.value = index < oldIndex ? 'slide-right' : 'slide-left'
      isActive.value = true
    }

    /**
     * @param {Number} oldIndex
     * @param {Number} index
     * @description Deactivate step, alter animation name based on the index.
     */
    const deactivate = (oldIndex, index) => {
      transitionName.value = index < oldIndex ? 'slide-right' : 'slide-left'
      isActive.value = false
    }

    /**
     * @description calls the parent startTransition function when the transition
     * animation starts
     */
    const startTransition = () => {
      /* istanbul ignore else */
      if (parent && typeof parent.startTransition === 'function') {
        parent.startTransition()
      }
    }

    /**
     * @description calls the parent endTransition function when the transition
     * animation stops
     */
    const endTransition = () => {
      /* istanbul ignore else */
      if (parent && typeof parent.endTransition === 'function') {
        parent.endTransition()
      }
    }

    const inst = {
      isActive,
      transitionName,
      isStepItem,
      root,
      activate,
      deactivate,
      animated,
      startTransition,
      endTransition,
      uid,
    }

    onMounted(() => {
      /* istanbul ignore else */
      if (parent && typeof parent.registerStep === 'function') {
        const computedInst = computedInstance(props, inst)
        parent.registerStep(computedInst)
      }
    })

    onBeforeUnmount(() => {
      /* istanbul ignore else */
      if (parent && typeof parent.unregisterStep === 'function') {
        parent.unregisterStep(inst.uid)
      }
    })

    return inst
  },
})
</script>
